import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useSettings } from "@/context/SettingsContext";

const LINKS = [
  { label: "Inicio", target: "#inicio" },
  { label: "Portafolio", target: "#portafolio" },
  { label: "Servicios", target: "#servicios" },
  { label: "Paquetes", target: "#paquetes" },
  { label: "Sobre mí", target: "#sobre-mi" },
  { label: "Reservar", target: "/reservar" },
  { label: "Contacto", target: "#contacto" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const settings = useSettings();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    if (target.startsWith("/")) {
      navigate(target);
      return;
    }
    if (location.pathname !== "/") {
      navigate("/" + target);
      setTimeout(() => document.querySelector(target)?.scrollIntoView({ behavior: "smooth" }), 250);
    } else {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const onHome = location.pathname === "/";
  const solid = scrolled || !onHome || open;
  const logoUrl = settings?.brand?.logo_url;

  return (
    <header
      data-testid="main-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        solid ? "bg-cream/90 backdrop-blur-md border-b border-gold/20 py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-16 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go("#inicio")}
          className="flex items-center gap-3 group"
        >
          {logoUrl && solid ? (
            <img src={logoUrl} alt="Yocaira Abreu Photography" className="h-9 sm:h-11 w-auto object-contain" />
          ) : (
            <span className={`font-serif text-xl sm:text-2xl tracking-tight leading-none ${solid ? "text-ink" : "text-white"}`}>
              Yocaira Abreu
              <span className="block overline-label mt-0.5">Photography</span>
            </span>
          )}
        </button>

        <nav className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.label}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s|í/g, "")}`}
              onClick={() => go(l.target)}
              className={`text-sm font-medium tracking-wide transition-colors duration-300 hover:text-gold ${
                solid ? "text-ink-soft" : "text-white/90"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-reservar-cta"
            onClick={() => navigate("/reservar")}
            className="px-5 py-2.5 rounded-full bg-ink text-cream text-sm font-medium tracking-wide hover:bg-gold hover:text-ink transition-all duration-300"
          >
            Reservar Sesión
          </button>
          <button
            data-testid="nav-admin-btn"
            onClick={() => navigate("/admin")}
            className={`px-4 py-2.5 rounded-full border text-sm font-medium tracking-wide transition-all duration-300 ${solid ? "border-gold text-gold hover:bg-gold hover:text-ink" : "border-white/60 text-white hover:bg-white hover:text-ink"}`}
          >
            Administrar sitio
          </button>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className={`lg:hidden ${solid ? "text-ink" : "text-white"}`}
          onClick={() => setOpen((o) => !o)}
          aria-label="Menú"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="lg:hidden bg-cream border-t border-gold/20 mt-3 animate-fade-up">
          <div className="px-6 py-4 flex flex-col gap-1">
            {LINKS.map((l) => (
              <button
                key={l.label}
                data-testid={`mobile-link-${l.label.toLowerCase().replace(/\s|í/g, "")}`}
                onClick={() => go(l.target)}
                className="text-left py-3 text-ink font-medium border-b border-beige last:border-0 hover:text-gold transition-colors"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="mobile-reservar-cta"
              onClick={() => { setOpen(false); navigate("/reservar"); }}
              className="mt-3 px-5 py-3 rounded-full bg-ink text-cream font-medium text-center"
            >
              Reservar Sesión
            </button>
            <button
              data-testid="mobile-admin-btn"
              onClick={() => { setOpen(false); navigate("/admin"); }}
              className="mt-2 px-5 py-3 rounded-full border border-gold text-gold font-medium text-center"
            >
              Administrar sitio
            </button>
          </div>
        </div>
      )}
    </header>
  );
};
